import { useAuth as useClerkAuth, useUser as useClerkUser } from '@clerk/clerk-react';

export const PLAYWRIGHT_AUTH_STORAGE_KEY = '__abl_playwright_auth__';
const isClerkAvailable = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);
export const isClerkAuthConfigured = isClerkAvailable;
const PREVIEW_AUTH_PARAM = 'preview';

interface PlaywrightAuthState {
  role: 'admin' | 'member';
  userId: string;
  email: string;
  firstName: string;
  token: string;
}

export type GetToken = (options?: {
  skipCache?: boolean;
  template?: string;
}) => Promise<string | null | undefined>;

export const CONVEX_SESSION_ERROR_MESSAGE =
  "We couldn't verify your Lab session. Refresh to sign back in.";

interface ConvexAuthTokenOptions {
  skipCache?: boolean;
}

interface AppAuthState {
  getToken: GetToken;
  isLoaded: boolean;
  isSignedIn: boolean;
  userId: string | null;
}

interface AppUser {
  id?: string | null;
  firstName?: string | null;
  imageUrl?: string | null;
  primaryEmailAddress?: {
    emailAddress?: string | null;
  } | null;
  externalAccounts?: Array<{
    provider?: string | null;
    imageUrl?: string | null;
  }> | null;
}

interface AppUserState {
  user: AppUser | null;
  isLoaded: boolean;
  isSignedIn: boolean;
}

const fallbackAuthState: AppAuthState = {
  getToken: async () => undefined,
  isLoaded: true,
  isSignedIn: false,
  userId: null,
};

const fallbackUserState: AppUserState = {
  user: null,
  isLoaded: true,
  isSignedIn: false,
};

const getPlaywrightToken: GetToken = async () => readPlaywrightAuthState()?.token;

export async function getConvexAuthToken(
  getToken?: GetToken | null,
  options: ConvexAuthTokenOptions = {},
): Promise<string | null> {
  if (!getToken) {
    return null;
  }

  try {
    const token = await getToken({
      template: 'convex',
      ...(options.skipCache ? { skipCache: true } : {}),
    });
    return token ?? null;
  } catch {
    return null;
  }
}

export async function requireConvexAuthToken(
  getToken?: GetToken | null,
  options: ConvexAuthTokenOptions = {},
): Promise<string> {
  const token = await getConvexAuthToken(getToken, options);
  if (!token) {
    throw new Error(CONVEX_SESSION_ERROR_MESSAGE);
  }
  return token;
}

function buildPlaywrightAuthState(role: PlaywrightAuthState['role']): PlaywrightAuthState {
  return {
    role,
    userId: role === 'admin' ? 'user-admin' : 'user-member',
    email: role === 'admin'
      ? 'admin+playwright@theaibrandlab.test'
      : 'member+playwright@theaibrandlab.test',
    firstName: role === 'admin' ? 'Admin' : 'Member',
    token: `playwright-${role}-token`,
  };
}

function seedPreviewAuthState(): PlaywrightAuthState | null {
  if (isClerkAvailable || !import.meta.env.DEV || typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const requestedRole = params.get(PREVIEW_AUTH_PARAM);
  if (requestedRole !== 'admin' && requestedRole !== 'member') {
    return null;
  }

  const nextState = buildPlaywrightAuthState(requestedRole);
  try {
    window.localStorage.setItem(PLAYWRIGHT_AUTH_STORAGE_KEY, JSON.stringify(nextState));
  } catch {
    return null;
  }

  return nextState;
}

function readPlaywrightAuthState(): PlaywrightAuthState | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const previewAuth = seedPreviewAuthState();
  if (previewAuth) {
    return previewAuth;
  }

  try {
    const raw = window.localStorage.getItem(PLAYWRIGHT_AUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<PlaywrightAuthState> | null;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    if (
      (parsed.role !== 'admin' && parsed.role !== 'member') ||
      typeof parsed.userId !== 'string' ||
      typeof parsed.email !== 'string' ||
      typeof parsed.firstName !== 'string' ||
      typeof parsed.token !== 'string'
    ) {
      return null;
    }

    return {
      role: parsed.role,
      userId: parsed.userId,
      email: parsed.email,
      firstName: parsed.firstName,
      token: parsed.token,
    };
  } catch {
    return null;
  }
}

export function getPlaywrightAuthState() {
  return readPlaywrightAuthState();
}

export function useAppAuth(): AppAuthState {
  if (!isClerkAvailable) {
    const playwrightAuth = readPlaywrightAuthState();
    if (!playwrightAuth) {
      return fallbackAuthState;
    }

    return {
      getToken: getPlaywrightToken,
      isLoaded: true,
      isSignedIn: true,
      userId: playwrightAuth.userId,
    };
  }

  try {
    const auth = useClerkAuth();
    return {
      getToken: auth.getToken,
      isLoaded: auth.isLoaded,
      isSignedIn: Boolean(auth.isSignedIn),
      userId: auth.userId ?? null,
    };
  } catch {
    const playwrightAuth = readPlaywrightAuthState();
    if (!playwrightAuth) {
      return fallbackAuthState;
    }

    return {
      getToken: getPlaywrightToken,
      isLoaded: true,
      isSignedIn: true,
      userId: playwrightAuth.userId,
    };
  }
}

export function useAppUser(): AppUserState {
  if (!isClerkAvailable) {
    const playwrightAuth = readPlaywrightAuthState();
    if (!playwrightAuth) {
      return fallbackUserState;
    }

    return {
      user: {
        id: playwrightAuth.userId,
        firstName: playwrightAuth.firstName,
        primaryEmailAddress: {
          emailAddress: playwrightAuth.email,
        },
      },
      isLoaded: true,
      isSignedIn: true,
    };
  }

  try {
    const { user, isLoaded, isSignedIn } = useClerkUser();
    return {
      user: user
        ? {
            id: user.id,
            firstName: user.firstName,
            imageUrl: user.imageUrl,
            primaryEmailAddress: user.primaryEmailAddress
              ? { emailAddress: user.primaryEmailAddress.emailAddress }
              : null,
            externalAccounts: user.externalAccounts?.map((account) => ({
              provider: account.provider,
              imageUrl: account.imageUrl,
            })) ?? null,
          }
        : null,
      isLoaded,
      isSignedIn: Boolean(isSignedIn),
    };
  } catch {
    const playwrightAuth = readPlaywrightAuthState();
    if (!playwrightAuth) {
      return fallbackUserState;
    }

    return {
      user: {
        id: playwrightAuth.userId,
        firstName: playwrightAuth.firstName,
        primaryEmailAddress: {
          emailAddress: playwrightAuth.email,
        },
      },
      isLoaded: true,
      isSignedIn: true,
    };
  }
}
